import { useCallback, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import {
  coreActions,
  coreSelectors,
  WorkoutEvent,
  ExerciseResult,
} from '@gymcloud/core';

import {
  uiActions,
} from 'web-actions';
import {
  getUiState,
} from 'web-selectors';
import {
  Button,
  Tooltip,
  ExercisePropertyLabel,
} from 'web-components';

import { Flex, Text, SVG, icons } from 'web-styled';

import AddResultForms from './AddResultForms';
import * as S from './styled';

interface Props {
  // We need to display a tooltip for the first item.
  isFirst: boolean
  event: WorkoutEvent;
  exercise: ExerciseResult;
}

const Exercise = ({ isFirst, event, exercise }: Props) => {
  const resourceType = 'exerciseResults';
  const mapState = useCallback(
    state => ({
      settings: coreSelectors.getProfile('user_settings')(state),
      results: coreSelectors.getExerciseResults(exercise.workout_exercise_id)(
        state,
      ),
      properties: coreSelectors.getExerciseProperties(
        exercise.workout_exercise_id,
      )(state),
      openedTooltips: getUiState('openedTooltips')(state),
    }),
    [exercise.id],
  );

  const {
    results,
    settings,
    properties,
    openedTooltips,
  } = useSelector(mapState, shallowEqual);
  const dispatch = useDispatch();

  const [isVisibleForm, setVisibleForm] = useState(false);

  const isTooltipVisible = isFirst && openedTooltips.some(el => el.key === 'add_results')
  const handleNewResult = (items: any[]) => {
    if (isTooltipVisible) {
      dispatch(
        uiActions.setUiState({
          openedTooltips: [
            {
              key: 'finish_workout'
            }
          ],
        }),
      );
    }
    dispatch(
      coreActions.requestCreateWorkoutEventResult({
        items,
        workoutExerciseId: exercise.workout_exercise_id,
        eventId: event.id,
      }),
    );
  };

  const handleUpdateResult = (payload: any, resultId: number) => {
    dispatch(
      coreActions.requestUpdateEntity({
        id: resultId,
        payload,
        resourceType,
      }),
    );
  };

  const handleDeleteResult = (resultId: number) => {
    dispatch(
      coreActions.requestDeleteEntity({
        id: resultId,
        resourceType,
      }),
    );
  };

  const renderButton = () => {
    if (!properties.length) {
      return (
        <Text color="#9ea0a1">
          No results metrics assigned
        </Text>
      )
    }

    const button = (
      <Button
        ghost
        icon={<SVG.Plus size={14} strokeWidth={4} />}
        appearance="primary"
        onClick={() => {
          setVisibleForm(true);
        }}
      >
        Add new results
      </Button>
    )

    return isTooltipVisible ? (
      <Tooltip
        body="Click this button under an exercise to enter results."
        toggleMode
        initialState
      >
        {button}
      </Tooltip>
    ) : button
  }

  return (
    <S.Exercise>
      <S.ExerciseHeader>
        <Flex shrink="0">
          <S.NumberContainer number={exercise.number}>
            {exercise.order_name}
          </S.NumberContainer>
          <S.ExerciseName>{exercise.name}</S.ExerciseName>
        </Flex>
        <Flex wrap="wrap" spacing={15}>
          {properties?.map(prop => (
            <Flex key={prop.id} align="center" spacing={5} padding="2px 0">
              <img src={icons.labelProperty} alt="" />
              <ExercisePropertyLabel property={prop} />
            </Flex>
          ))}
        </Flex>
      </S.ExerciseHeader>
      <Flex width="100%" column>
        <S.Results>
          {results?.map(result => (
            <S.Result key={result.id}>
              <Tooltip
                body="Enter your personal best by clicking this icon"
                positions={['top']}
              >
                <S.PersonalBest
                  selected={result.is_personal_best}
                  onClick={() => {
                    handleUpdateResult(
                      {
                        is_personal_best: !result.is_personal_best,
                      },
                      result.id,
                    );
                  }}
                >
                  <S.PersonalBestIcon />
                  <span>Personal Best</span>
                </S.PersonalBest>
              </Tooltip>
              <img src={icons.labelProperty} alt="" />
              <Flex wrap="wrap" spacing={18}>
                {result.items?.map(resultItem => (
                  <ExercisePropertyLabel
                    key={resultItem.id}
                    property={resultItem}
                  />
                ))}
              </Flex>
              <span
                className="trashcan"
                onClick={() => {
                  handleDeleteResult(result.id);
                }}
              >
                <SVG.TrashAlt size={20} stroke="#ced0d0" />
              </span>
            </S.Result>
          ))}
        </S.Results>
        {isVisibleForm && (
          <AddResultForms
            units={settings.units_system}
            isTooltipVisible={isTooltipVisible}
            properties={properties}
            onSubmit={(items: any[]) => {
              handleNewResult(items);
              setVisibleForm(false);
            }}
            onClose={() => {
              setVisibleForm(false);
            }}
          />
        )}
      </Flex>
      <S.ExerciseFooter>
        {renderButton()}
      </S.ExerciseFooter>
    </S.Exercise>
  );
};

export default Exercise;
